// frontend/src/utils/api.js
// ============================================================
// PAWNI — API Client
// Centralized HTTP client with auth token injection,
// automatic token refresh, and error handling.
// ============================================================

import { API_ENDPOINTS, STORAGE_KEYS } from "../config";

// ── Token Management ───────────────────────────────────────

export function getAccessToken() {
  return localStorage.getItem(STORAGE_KEYS.accessToken) || "";
}

export function getRefreshToken() {
  return localStorage.getItem(STORAGE_KEYS.refreshToken) || "";
}

export function setTokens(accessToken, refreshToken) {
  localStorage.setItem(STORAGE_KEYS.accessToken, accessToken);
  localStorage.setItem(STORAGE_KEYS.refreshToken, refreshToken);
}

export function clearTokens() {
  localStorage.removeItem(STORAGE_KEYS.accessToken);
  localStorage.removeItem(STORAGE_KEYS.refreshToken);
  localStorage.removeItem(STORAGE_KEYS.user);
}

// ── Core Fetch Wrapper ─────────────────────────────────────

const FETCH_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

async function apiFetch(url, options = {}) {
  const token = getAccessToken();

  const headers = {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  // Remove Content-Type for FormData (browser sets it with boundary)
  if (options.body instanceof FormData) {
    delete headers["Content-Type"];
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers,
      signal: options.signal || controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  // Handle 401 — attempt token refresh
  if (response.status === 401 && token) {
    const refreshed = await attemptTokenRefresh();
    if (refreshed) {
      // Retry with new token
      const newToken = getAccessToken();
      headers.Authorization = `Bearer ${newToken}`;
      const retryResponse = await fetch(url, { ...options, headers });
      return handleResponse(retryResponse);
    } else {
      // Refresh failed — clear tokens and redirect
      clearTokens();
      window.dispatchEvent(new CustomEvent("pawni:auth-expired"));
      throw new ApiError("Session expired. Please log in again.", 401);
    }
  }

  return handleResponse(response);
}

async function handleResponse(response) {
  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const errorData = await response.json();
      const detail = errorData.detail;
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors: [{loc: [...], msg: "...", type: "..."}]
        errorMessage = detail
          .map((d) => (typeof d === "string" ? d : d.msg || JSON.stringify(d)))
          .join("; ");
      } else if (detail && typeof detail === "object") {
        errorMessage = detail.message || detail.msg || JSON.stringify(detail);
      } else if (typeof errorData.error === "string") {
        errorMessage = errorData.error;
      } else if (typeof errorData.message === "string") {
        errorMessage = errorData.message;
      }
    } catch {
      // Response wasn't JSON
    }
    throw new ApiError(errorMessage, response.status);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

// POST /api/auth/refresh with JSON {refresh_token}. The backend rotates the
// refresh token (the old one is revoked), so the new pair must be stored.
// Concurrent 401s share one in-flight refresh so a rotated token is never
// replayed.
let _refreshInFlight = null;

async function attemptTokenRefresh() {
  if (_refreshInFlight) return _refreshInFlight;

  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;

  _refreshInFlight = (async () => {
    try {
      const response = await fetch(API_ENDPOINTS.refresh, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!response.ok) return false;

      const data = await response.json();
      if (!data?.access_token) return false;
      setTokens(data.access_token, data.refresh_token || refreshToken);
      if (data.user) {
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
      }
      return true;
    } catch {
      return false;
    } finally {
      _refreshInFlight = null;
    }
  })();

  return _refreshInFlight;
}

// ── Custom Error Class ─────────────────────────────────────

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// ============================================================
// AUTH API
// ============================================================

export const authApi = {
  signup: (email, password, displayName, secretCode) =>
    apiFetch(API_ENDPOINTS.signup, {
      method: "POST",
      body: JSON.stringify({
        email,
        password,
        display_name: displayName,
        secret_code: secretCode,
      }),
    }),

  login: (email, password) =>
    apiFetch(API_ENDPOINTS.login, {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  logout: () =>
    apiFetch(API_ENDPOINTS.logout, { method: "POST" }).catch(() => null),

  getMe: () => apiFetch(API_ENDPOINTS.me),
};

// ============================================================
// SESSIONS API
// ============================================================
// Use sessions base with trailing slash to avoid 307 redirect
// (fixes mixed content / HTTPS issues on Vercel + Railway).
const SESSIONS_BASE = `${API_ENDPOINTS.sessions.replace(/\/?$/, "")}/`;

const jsonInit = (method, body) => ({
  method,
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

export const sessionsApi = {
  /** POST /api/sessions/ {title?, step_mode?} -> Session */
  create: ({ title, step_mode } = {}) => {
    const body = {};
    if (title) body.title = title;
    if (typeof step_mode === "boolean") body.step_mode = step_mode;
    return apiFetch(SESSIONS_BASE, jsonInit("POST", body));
  },

  /** GET /api/sessions/ -> {sessions, total} */
  list: ({ limit = 50, offset = 0, includeArchived = false } = {}) =>
    apiFetch(`${SESSIONS_BASE}?limit=${limit}&offset=${offset}&include_archived=${includeArchived}`),

  /** GET /api/sessions/{id} -> SessionDetail */
  get: (sessionId) => apiFetch(API_ENDPOINTS.session(sessionId)),

  /** PATCH /api/sessions/{id} {title?, is_archived?, step_mode?} -> Session */
  update: (sessionId, data) => apiFetch(API_ENDPOINTS.session(sessionId), jsonInit("PATCH", data)),

  /** DELETE /api/sessions/{id} -> {message} */
  delete: (sessionId) => apiFetch(API_ENDPOINTS.session(sessionId), { method: "DELETE" }),

  /** GET /api/sessions/{id}/messages -> {messages} */
  getMessages: (sessionId, { limit = 500, offset = 0 } = {}) =>
    apiFetch(`${API_ENDPOINTS.sessionMessages(sessionId)}?limit=${limit}&offset=${offset}`),
};

// ============================================================
// CHAT / PIPELINE API
// ============================================================

export const chatApi = {
  /** POST /message {content, answers?} -> 202 {run_id, stage} */
  sendMessage: (sessionId, content, answers) =>
    apiFetch(
      API_ENDPOINTS.chatMessage(sessionId),
      jsonInit("POST", answers && Object.keys(answers).length ? { content, answers } : { content })
    ),

  /** POST /advance {action} -> 202 {run_id, stage} */
  advance: (sessionId, action) => apiFetch(API_ENDPOINTS.chatAdvance(sessionId), jsonInit("POST", { action })),

  /** POST /cancel -> {cancelled} */
  cancel: (sessionId) => apiFetch(API_ENDPOINTS.chatCancel(sessionId), { method: "POST" }),

  /** POST /revert {agent} -> SessionDetail */
  revert: (sessionId, agent) => apiFetch(API_ENDPOINTS.chatRevert(sessionId), jsonInit("POST", { agent })),

  /** POST /rerun {agent} -> 202 {run_id} */
  rerun: (sessionId, agent) => apiFetch(API_ENDPOINTS.chatRerun(sessionId), jsonInit("POST", { agent })),

  /** GET /status -> PipelineStatus */
  getStatus: (sessionId) => apiFetch(API_ENDPOINTS.chatStatus(sessionId)),

  /** GET /result -> Result */
  getResult: (sessionId) => apiFetch(API_ENDPOINTS.chatResult(sessionId)),
};

// ============================================================
// PUBLIC / HEALTH API
// ============================================================

export const publicApi = {
  /** GET /api/public/config */
  config: () => apiFetch(API_ENDPOINTS.publicConfig),
};

export const healthApi = {
  check: () => apiFetch(API_ENDPOINTS.health),
};

// ============================================================
// SSE EVENT STREAM
// ============================================================
// EventSource cannot send an Authorization header, so the stream is read with
// fetch + ReadableStream and parsed by hand (standard SSE framing: `event:` and
// `data:` lines, frames separated by a blank line, data is JSON).

function parseFrame(frame) {
  let event = "message";
  const data = [];
  for (const line of frame.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const idx = line.indexOf(":");
    const field = idx === -1 ? line : line.slice(0, idx);
    let value = idx === -1 ? "" : line.slice(idx + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }
  if (!data.length) return null;
  const raw = data.join("\n");
  try {
    return { event, data: JSON.parse(raw) };
  } catch {
    return { event, data: raw };
  }
}

function abortableDelay(ms, signal) {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const finish = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal?.addEventListener("abort", finish, { once: true });
  });
}

function streamRequest(url, signal) {
  return fetch(url, {
    headers: { Accept: "text/event-stream", Authorization: `Bearer ${getAccessToken()}` },
    cache: "no-store",
    signal,
  });
}

/**
 * Open the session event stream and keep it open until `signal` aborts.
 *
 * handlers: { [eventName](data) } for status, agent_start, token, agent_end,
 *   message, coverage, waiting, result, error, done, ping. Lifecycle hooks:
 *   onOpen() after every (re)connect, onDisconnect(error|null) when the stream
 *   drops, onFatal(ApiError) when it gives up (auth expired, 403, 404).
 * Reconnects with exponential backoff (1s -> 30s). A 401 triggers one token
 * refresh attempt per connection attempt.
 * Returns { closed: Promise } that settles once the loop ends.
 */
export function openEventStream(sessionId, handlers = {}, { signal, minDelay = 1000, maxDelay = 30000 } = {}) {
  const url = API_ENDPOINTS.chatEvents(sessionId);
  const call = (name, arg) => {
    try {
      handlers[name]?.(arg);
    } catch (err) {
      // A handler bug must never kill the stream.
      console.error(`[PAWNI] SSE handler "${name}" failed`, err);
    }
  };

  const run = async () => {
    let delay = minDelay;
    while (!signal?.aborted) {
      try {
        let response = await streamRequest(url, signal);

        if (response.status === 401) {
          const ok = await attemptTokenRefresh();
          if (!ok) {
            clearTokens();
            window.dispatchEvent(new CustomEvent("pawni:auth-expired"));
            call("onFatal", new ApiError("Session expired. Please log in again.", 401));
            return;
          }
          response = await streamRequest(url, signal);
        }

        if (response.status === 401 || response.status === 403 || response.status === 404) {
          call("onFatal", new ApiError(response.status === 404 ? "Session not found." : "Not allowed.", response.status));
          return;
        }
        if (!response.ok || !response.body) {
          throw new ApiError(`Event stream failed (${response.status})`, response.status);
        }

        delay = minDelay;
        call("onOpen");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n?/g, "\n");
          let sep = buffer.indexOf("\n\n");
          while (sep !== -1) {
            const parsed = parseFrame(buffer.slice(0, sep));
            buffer = buffer.slice(sep + 2);
            if (parsed) call(parsed.event, parsed.data);
            sep = buffer.indexOf("\n\n");
          }
        }
        if (signal?.aborted) return;
        call("onDisconnect", null);
      } catch (err) {
        if (signal?.aborted || err?.name === "AbortError") return;
        call("onDisconnect", err);
      }
      await abortableDelay(delay, signal);
      delay = Math.min(delay * 2, maxDelay);
    }
  };

  return { closed: run() };
}
